/**
 * useWorkspaceRouter — applies a `RouteDecision` to TanStack Router + Zustand.
 *
 * This is the central call site that bridges the AI/BE-driven routing layer
 * with the SPA's actual URL + state. Three responsibilities:
 *
 *   1. `navigateToDecision(decision)` — flip the workspace tab + write a deep
 *      link to the URL so refresh / share work. Pushes the previous tab onto
 *      the pivot-history stack so the user can revert.
 *
 *   2. `revertLastPivot()` — pop the top of the stack and navigate back. No-op
 *      when the stack is empty (the UndoChip should hide in that case).
 *
 *   3. `currentTab` — re-export the live store selector so consumers don't
 *      have to import the store directly.
 *
 * Why not co-locate this with `workspaceStore`?
 *   - The store stays framework-agnostic (no TanStack Router import).
 *   - The hook owns the navigation side-effect; the store owns the data.
 *   - Tests can render `useWorkspaceRouter` inside a memory router without
 *     touching the BFF / live URL.
 *
 * URL contract (matches Phase 4.E typed search params):
 *   /workspace/finance?focus=invoice-123&filter=overdue&sub=banking
 *
 *   - `sub`   → RouteDecision.surface (sub-tab id within the tab)
 *   - `focus` → optional record-id passthrough (filters.focus)
 *   - any other filters → forwarded as URL search params unchanged.
 *
 * May 26, 2026 — Canvas + Dashboard promoted to first-class tabs, so the
 * legacy ``rail`` param (formerly carrying ``outputRail`` decisions) is
 * gone. Any deep-link to Canvas/Dashboard now resolves through the tab
 * route directly.
 */

import { useCallback } from "react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import type { RouteDecision, WorkspaceTab } from "@ando/core/domain";
import { getWorkspaceTabDef } from "@/features/workspace/config";
import {
  selectActiveTab,
  selectPopPivot,
  selectPushPivot,
  selectRouteFromAndo,
  selectRouteWithSubTab,
  selectSetTab,
  useWorkspaceStore,
  type PivotHistoryEntry,
} from "../state/workspaceStore";

export interface WorkspaceRouterApi {
  /** Currently active tab (reactive — re-renders on change). */
  currentTab: WorkspaceTab;
  /**
   * Apply a `RouteDecision`: navigate + record on the pivot stack.
   * Returns `true` if the navigation fired, `false` when the decision
   * target equals the current tab AND has no new surface/filters
   * (avoids spurious URL churn + redundant undo chips).
   */
  navigateToDecision: (decision: RouteDecision) => boolean;
  /**
   * Pop the last Ando pivot and navigate back. Returns the popped entry
   * (consumer logs it / drives toast copy) or `null` when the stack is empty.
   */
  revertLastPivot: () => PivotHistoryEntry | null;
}

/**
 * Build the search-param record TanStack Router expects from a decision.
 * Pure helper so it can be unit-tested without a router instance.
 */
export function decisionToSearchParams(
  decision: RouteDecision,
): Record<string, string> {
  const params: Record<string, string> = {};
  if (decision.surface !== undefined) params.sub = decision.surface;
  if (decision.filters !== undefined) {
    for (const [key, value] of Object.entries(decision.filters)) {
      if (value === undefined || value === null) continue;
      // Filters are forwarded as primitive strings (URL search-param-native).
      // Complex shapes (objects/arrays) round-trip via JSON — extremely rare in
      // practice, the BE keeps hints flat.
      params[key] =
        typeof value === "string" ? value : JSON.stringify(value);
    }
  }
  return params;
}

export function useWorkspaceRouter(): WorkspaceRouterApi {
  const navigate = useNavigate();
  const router = useRouter();
  const currentTab = useWorkspaceStore(selectActiveTab);
  const setTab = useWorkspaceStore(selectSetTab);
  const routeFromAndo = useWorkspaceStore(selectRouteFromAndo);
  const routeWithSubTab = useWorkspaceStore(selectRouteWithSubTab);
  const pushPivot = useWorkspaceStore(selectPushPivot);
  const popPivot = useWorkspaceStore(selectPopPivot);

  const navigateToDecision = useCallback(
    (decision: RouteDecision): boolean => {
      const def = getWorkspaceTabDef(decision.tab);
      if (!def) {
        // Unknown tab — bail loud rather than navigate to a non-existent
        // route (which TanStack would catch at the navigate() boundary
        // anyway, but throwing here surfaces the bug at the call site).
        throw new Error(
          `[workspace-router] no tab def for "${decision.tab}" — extend WORKSPACE_TAB_DEFS`,
        );
      }

      const fromTab = useWorkspaceStore.getState().currentTab;
      const search = decisionToSearchParams(decision);
      const sameTab = fromTab === decision.tab;
      const hasSurfaceOrFilters = Object.keys(search).length > 0;

      // Skip the navigation when the decision is a no-op (same tab, no
      // surface / filters / rail). Caller logs nothing — UndoChip stays
      // hidden. Without this guard, an idempotent BE hint would spam the
      // history stack.
      if (sameTab && !hasSurfaceOrFilters) {
        return false;
      }

      // May 24, 2026 — Preserve focus guard (user-feedback bug):
      // se l'utente è già dentro a un DETTAGLIO focused (path con
      // ``?focus=``) nella STESSA tab in cui la decision punta, NON
      // strapparlo via per cambiare sub-tab/rail/filter. TanStack
      // ``navigate({to, search})`` rimpiazza l'intera search → il
      // ``focus`` esistente verrebbe cancellato (l'utente atterrerebbe
      // sulla lista generica). Cambio tab esplicito (sameTab=false)
      // resta non-vincolato: l'utente ha intenzionalmente lasciato il
      // focus quando passa di sezione. Replica del pattern in
      // ``useSourceAutoOpen`` + ``useActionRouter::handleProposal``.
      const currentSearch = router.state.location.search as
        | { focus?: unknown }
        | undefined;
      const currentFocus =
        typeof currentSearch?.focus === "string" && currentSearch.focus
          ? currentSearch.focus
          : null;
      if (sameTab && currentFocus !== null) {
        return false;
      }

      // Push BEFORE navigating so a synchronous failure during navigate()
      // still records the user's intent (revert works).
      pushPivot({
        fromTab,
        toTab: decision.tab,
        timestamp: decision.timestamp,
        decision,
      });

      // Flip the store first — TanStack `navigate({ to, search })` is async
      // (returns a Promise) and consumers expect the visual cue immediately.
      // When the decision carries a ``surface`` (sub-tab) the store gets a
      // single atomic update via ``routeWithSubTab`` so the chip selection
      // and the orange pulse light up together; otherwise fall back to
      // ``routeFromAndo`` which only flips the top tab.
      if (decision.surface !== undefined) {
        routeWithSubTab(decision.tab, decision.surface);
      } else {
        routeFromAndo(decision.tab);
      }

      // TanStack Router types the `to` argument against the generated
      // route tree; `def.route` is the matching literal.
      void navigate({
        to: def.route,
        search,
      });

      return true;
    },
    [navigate, pushPivot, routeFromAndo, routeWithSubTab, router],
  );

  const revertLastPivot = useCallback((): PivotHistoryEntry | null => {
    const popped = popPivot();
    if (popped === null) return null;

    const def = getWorkspaceTabDef(popped.fromTab);
    if (!def) {
      // If the previous tab def disappeared (extremely unlikely — would
      // mean WORKSPACE_TAB_DEFS shrank mid-session), bail without
      // navigation but still return the entry so the UI can clean up.
      return popped;
    }

    // Reverting is a *manual* transition by definition — clears the
    // "Aperto da Ando" pulse + label so the user sees a clean tab.
    setTab(popped.fromTab, "manual");
    void navigate({ to: def.route, search: {} });

    return popped;
  }, [navigate, popPivot, setTab]);

  return {
    currentTab,
    navigateToDecision,
    revertLastPivot,
  };
}
